import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import * as VisuallyHidden from '@radix-ui/react-visually-hidden'

import { cn } from '@/lib/css-utils'

const Dialog = function DialogPrimitiveRoot(
  props: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root>,
) {
  return <DialogPrimitive.Root {...props} />
}

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-bg/80 backdrop-blur-xs',
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      'data-[state=closed]:fade-out data-[state=open]:fade-in',
      className,
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    hasCloseButton?: boolean
    disableOverlayClose?: boolean
    title: string // we need title to ensure to pass a11y requirements
  }
>(
  (
    {
      className,
      children,
      hasCloseButton = true,
      disableOverlayClose = false,
      title,
      ...props
    },
    ref,
  ) => (
    <DialogPortal>
      <DialogOverlay className="blur-xs bg-black-10 opacity-[40%] dark:opacity-[70%]" />
      <DialogPrimitive.Content
        onPointerDownOutside={(e) => {
          if (disableOverlayClose) {
            e.preventDefault()
          }
        }}
        ref={ref}
        className={cn(
          'fixed left-[50%] top-[50%] z-50 grid w-[90vw] sm:w-full max-w-lg translate-x-[-50%]',
          'translate-y-[-50%] gap-4 border border-background-muted p-6 shadow-lg duration-200',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95',
          'data-[state=open]:zoom-in-95',
          'rounded-[1rem] bg-modal-bg',
          className,
        )}
        {...props}
      >
        <VisuallyHidden.Root>
          <DialogTitle>{title}</DialogTitle>
        </VisuallyHidden.Root>
        {children}
        {hasCloseButton && (
          <DialogPrimitive.Close
            className="absolute right-6 top-6 rounded-sm opacity-70
       ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden
        focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none
         data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
            aria-label="close dialog"
          >
            <X className="h-5 w-5 text-copy-primary" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  ),
)
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col space-y-1.5 text-center sm:text-left',
      className,
    )}
    {...props}
  />
)
DialogHeader.displayName = 'DialogHeader'

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
      className,
    )}
    {...props}
  />
)
DialogFooter.displayName = 'DialogFooter'

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-lg text-copy-primary font-semibold leading-none tracking-tight pr-4',
      className,
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-copy-accent', className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
